import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  FiHardDrive,
  FiRefreshCw,
  FiPlay,
  FiSquare,
  FiSettings,
  FiClock,
  FiCheckCircle,
  FiAlertTriangle,
  FiDatabase,
  FiList,
  FiActivity,
  FiSave,
  FiShield,
  FiInfo,
  FiTrash2,
  FiUsers,
} from 'react-icons/fi';

const FONT = "'Segoe UI', system-ui, -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#6366f1';
const ACCENT_2 = '#8b5cf6';
const ACCENT_SOFT = 'rgba(99, 102, 241, 0.1)';
const ACCENT_TEXT = '#4338ca';
const GLASS_BORDER = 'rgba(255, 255, 255, 0.78)';
const GLASS_EDGE = 'rgba(186, 196, 255, 0.35)';
const GLOSS_SHINE = 'inset 0 1px 0 rgba(255, 255, 255, 0.98), inset 0 -1px 0 rgba(255, 255, 255, 0.28)';
const GLOSS_FLOAT = '0 10px 36px rgba(99, 102, 241, 0.1), 0 2px 8px rgba(15, 23, 42, 0.04)';
const SHELL_BG = 'linear-gradient(160deg, #eef2ff 0%, #f8f7ff 35%, #f5f3ff 68%, #f0f9ff 100%)';
const TEXT = '#0f172a';
const MUTED = '#64748b';
const STRONG = '#475569';
const BODY = '#64748b';
const WARN = '#c2410c';
const OK = '#047857';
const DANGER = '#dc2626';
const GLOSS_SURFACE = 'linear-gradient(165deg, rgba(255,255,255,0.92) 0%, rgba(248,250,255,0.78) 45%, rgba(237,233,254,0.52) 100%)';
const INPUT_BG = 'linear-gradient(180deg, rgba(255,255,255,0.96) 0%, rgba(241,245,255,0.82) 100%)';
const CODE_BG = 'linear-gradient(180deg, rgba(255,255,255,0.88) 0%, rgba(248,250,255,0.72) 100%)';

const CARD = {
  default: {
    background: GLOSS_SURFACE,
    border: `1px solid ${GLASS_BORDER}`,
    boxShadow: `${GLOSS_FLOAT}, ${GLOSS_SHINE}`,
  },
  warn: {
    background: 'linear-gradient(165deg, rgba(255,255,255,0.94) 0%, rgba(255,251,235,0.82) 50%, rgba(254,243,199,0.55) 100%)',
    border: '1px solid rgba(255, 255, 255, 0.75)',
    boxShadow: '0 10px 32px rgba(245, 158, 11, 0.08), inset 0 1px 0 rgba(255,255,255,0.98), inset 0 -1px 0 rgba(255,255,255,0.25)',
  },
  danger: {
    background: 'linear-gradient(165deg, rgba(255,255,255,0.94) 0%, rgba(254,242,242,0.78) 55%, rgba(254,226,226,0.5) 100%)',
    border: '1px solid rgba(255, 255, 255, 0.75)',
    boxShadow: '0 10px 32px rgba(239, 68, 68, 0.07), inset 0 1px 0 rgba(255,255,255,0.98), inset 0 -1px 0 rgba(255,255,255,0.25)',
  },
  accent: {
    background: 'linear-gradient(165deg, rgba(255,255,255,0.94) 0%, rgba(237,233,254,0.78) 50%, rgba(224,231,255,0.55) 100%)',
    border: '1px solid rgba(255, 255, 255, 0.78)',
    boxShadow: '0 10px 36px rgba(99, 102, 241, 0.12), inset 0 1px 0 rgba(255,255,255,0.98), inset 0 -1px 0 rgba(255,255,255,0.28)',
  },
  success: {
    background: 'linear-gradient(165deg, rgba(255,255,255,0.94) 0%, rgba(236,253,245,0.78) 50%, rgba(209,250,229,0.52) 100%)',
    border: '1px solid rgba(255, 255, 255, 0.78)',
    boxShadow: '0 10px 32px rgba(16, 185, 129, 0.08), inset 0 1px 0 rgba(255,255,255,0.98), inset 0 -1px 0 rgba(255,255,255,0.25)',
  },
};

const LABEL_FIELD = { fontSize: 13, fontWeight: 600, color: STRONG, lineHeight: 1.45, letterSpacing: '0.005em' };
const HINT_TEXT = { fontSize: 13, color: BODY, lineHeight: 1.6, fontWeight: 500 };
const INPUT_STYLE = {
  padding: '11px 14px',
  borderRadius: 14,
  border: `1px solid ${GLASS_BORDER}`,
  background: INPUT_BG,
  color: TEXT,
  fontSize: 15,
  fontWeight: 500,
  fontFamily: FONT,
  boxShadow: `${GLOSS_SHINE}, 0 2px 10px rgba(99, 102, 241, 0.05)`,
  outline: 'none',
  backdropFilter: 'blur(8px)',
};
const CODE_BLOCK = {
  margin: '0 0 12px',
  padding: '12px 14px',
  borderRadius: 14,
  background: CODE_BG,
  border: `1px solid ${GLASS_BORDER}`,
  color: STRONG,
  fontSize: 12,
  lineHeight: 1.55,
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-all',
  fontFamily: 'Consolas, "Cascadia Code", monospace',
  fontWeight: 500,
  boxShadow: `${GLOSS_SHINE}, 0 2px 8px rgba(99, 102, 241, 0.04)`,
  backdropFilter: 'blur(8px)',
};

function looksLikeMojibake(text) {
  const s = String(text || '');
  return /Р|Сѓ|С‡|С‘|РЅ|Р°|Рї|Р·|РІ|Рµ|СЂ|Р‘|Р­|СЂ|Ð|Ñ/.test(s);
}

function normalizeLogLine(line) {
  const s = String(line || '').replace(/^\uFEFF/, '');
  const tsMatch = s.match(/^(\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2})\s+(.*)$/);
  if (!tsMatch) return s;
  const [, ts, msg] = tsMatch;

  if (/===/.test(msg) && /\.db\s*->/i.test(msg) && /\.json/i.test(msg)) {
    const paths = msg.match(/([A-Z]:[\\][^\s]+?\.db)\s*->\s*([A-Z]:[\\][^\s]+?\.json)/i);
    if (paths) return `${ts}  === Accounts backup: ${paths[1]} -> ${paths[2]} ===`;
  }
  if (/\.json\s*$/i.test(msg) && !msg.trim().startsWith('{')) {
    const p = msg.match(/([A-Z]:[\\][^\s]+?\.json)/i);
    if (p) {
      const prefix = msg.slice(0, Math.max(0, msg.indexOf(p[1])));
      if (looksLikeMojibake(prefix) || /Export|Экспорт/i.test(msg)) {
        return `${ts}  Export finished: ${p[1]}`;
      }
    }
  }
  if (looksLikeMojibake(msg) && /(accounts_backup|\.json)/i.test(msg)) {
    const name = msg.match(/(accounts_backup[^\s]*\.json)/i);
    if (name && /удал|Removed|Удал/i.test(msg)) {
      return `${ts}  Removed old accounts backup: ${name[1]}`;
    }
  }

  if (looksLikeMojibake(msg)) {
    const pathOnly = msg.match(/([A-Z]:[\\][^\s]+)/);
    if (pathOnly && !/ERROR/i.test(msg)) return `${ts}  ${pathOnly[1]}`;
  }

  return s;
}

function classifyLogLine(line) {
  const s = normalizeLogLine(line);
  if (/ERROR|ОШИБКА|Failed|\bfail\b|Access Denied|cannot|не удалось/i.test(s)) return 'error';
  if (/WARN|WARNING|ВНИМАНИЕ/i.test(s)) return 'warn';
  if (/OK|Success|Succeeded|Completed|Успешно|healthy|Created|Added|Done|Deleted versions: 0|skip start/i.test(s)) return 'ok';
  if (/^===|^Starting|^Stopping|Cleanup|Setup|Backup|Export|successCount/i.test(s)) return 'info';
  return 'default';
}

const LOG_LINE_STYLE = {
  error: { color: DANGER, bg: 'rgba(254, 242, 242, 0.65)' },
  warn: { color: WARN, bg: 'rgba(254, 243, 199, 0.55)' },
  ok: { color: OK, bg: 'rgba(236, 253, 245, 0.65)' },
  info: { color: ACCENT_TEXT, bg: 'rgba(237, 233, 254, 0.45)' },
  default: { color: BODY, bg: 'transparent' },
};

const INNER_GLOSS = {
  padding: 16,
  borderRadius: 16,
  border: `1px solid ${GLASS_BORDER}`,
  background: 'linear-gradient(180deg, rgba(255,255,255,0.9) 0%, rgba(248,250,255,0.68) 100%)',
  boxShadow: `${GLOSS_SHINE}, 0 4px 14px rgba(99, 102, 241, 0.05)`,
  backdropFilter: 'blur(10px)',
};

function LogViewer({ lines, maxHeight = 280 }) {
  if (!lines?.length) {
    return <div style={{ color: MUTED, fontSize: 14 }}>—</div>;
  }
  return (
    <div
      style={{
        maxHeight,
        overflow: 'auto',
        borderRadius: 14,
        border: `1px solid ${GLASS_BORDER}`,
        background: CODE_BG,
        padding: '10px 12px',
        boxShadow: `${GLOSS_SHINE}, 0 4px 14px rgba(99, 102, 241, 0.05)`,
        backdropFilter: 'blur(10px)',
      }}
    >
      {lines.map((line, idx) => {
        const text = normalizeLogLine(line);
        const kind = classifyLogLine(line);
        const st = LOG_LINE_STYLE[kind] || LOG_LINE_STYLE.default;
        return (
          <div
            key={`${idx}-${text.slice(0, 24)}`}
            style={{
              fontSize: 14,
              lineHeight: 1.6,
              fontFamily: 'Consolas, "Cascadia Code", monospace',
              color: st.color,
              background: st.bg,
              padding: '4px 10px',
              borderRadius: 6,
              marginBottom: 3,
              wordBreak: 'break-word',
            }}
          >
            {text}
          </div>
        );
      })}
    </div>
  );
}

function CollapsibleLogBlock({ title, icon: Icon, lines, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const count = lines?.length || 0;
  return (
    <GlassPanel style={{ marginBottom: 12, padding: open ? 20 : '14px 20px' }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          border: 'none',
          background: 'transparent',
          cursor: 'pointer',
          padding: 0,
          textAlign: 'left',
          fontFamily: FONT,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {Icon ? <Icon size={17} color={ACCENT} /> : null}
          <span style={{ fontSize: 16, fontWeight: 600, color: TEXT, letterSpacing: '-0.01em' }}>{title}</span>
          <span
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: MUTED,
              background: 'linear-gradient(180deg, rgba(255,255,255,0.9), rgba(241,245,255,0.65))',
              padding: '2px 8px',
              borderRadius: 999,
              border: `1px solid ${GLASS_BORDER}`,
              boxShadow: GLOSS_SHINE,
            }}
          >
            {count || 0}
          </span>
        </div>
        <span style={{ fontSize: 13, fontWeight: 600, color: ACCENT_TEXT }}>{open ? 'Свернуть' : 'Развернуть'}</span>
      </button>
      {open ? (
        <div style={{ marginTop: 14 }}>
          <LogViewer lines={lines} />
        </div>
      ) : null}
    </GlassPanel>
  );
}

function ScheduleFields({ hour, minute, onHour, onMinute, disabled, timeLabel }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end' }}>
      <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <span style={LABEL_FIELD}>Часы</span>
        <input type="number" min={0} max={23} value={hour} disabled={disabled} onChange={(e) => onHour(Number(e.target.value))} style={{ ...INPUT_STYLE, width: 72 }} />
      </label>
      <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <span style={LABEL_FIELD}>Минуты</span>
        <input type="number" min={0} max={59} value={minute} disabled={disabled} onChange={(e) => onMinute(Number(e.target.value))} style={{ ...INPUT_STYLE, width: 72 }} />
      </label>
      {timeLabel ? (
        <div style={{ fontSize: 13, color: ACCENT_TEXT, fontWeight: 600, paddingBottom: 10 }}>{timeLabel}</div>
      ) : null}
    </div>
  );
}

function formatIsoRu(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime()) || d.getFullYear() < 2000) return '—';
  return d.toLocaleString('ru-RU');
}

function formatLastResult(value) {
  const r = String(value || '').trim();
  if (!r) return '—';
  const low = r.toLowerCase();
  if (low === 'succeeded' || low === 'completed' || low === 'success' || low === '0') return 'Успешно';
  if (low === 'failed' || low === 'failure') return 'Ошибка';
  if (low === 'in progress' || low === 'running') return 'Выполняется';
  return r;
}

function formatDuration(seconds) {
  if (seconds == null || !Number.isFinite(seconds)) return '—';
  const s = Math.max(0, Math.floor(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h} ч ${m} мин`;
  if (m > 0) return `${m} мин ${sec} сек`;
  return `${sec} сек`;
}

function formatGb(gb) {
  if (gb == null || !Number.isFinite(gb)) return '—';
  return `${gb} ГБ`;
}

function pad2(n) {
  return String(n).padStart(2, '0');
}

function GlassPanel({ children, style = {}, variant = 'default' }) {
  const skin = CARD[variant] || CARD.default;
  return (
    <div
      className="backup-gloss-card"
      style={{
        position: 'relative',
        borderRadius: 20,
        border: skin.border,
        background: skin.background,
        backdropFilter: 'blur(20px) saturate(1.35)',
        WebkitBackdropFilter: 'blur(20px) saturate(1.35)',
        boxShadow: skin.boxShadow,
        padding: 20,
        overflow: 'hidden',
        ...style,
      }}
    >
      <div className="backup-gloss-card-shine" aria-hidden />
      <div style={{ position: 'relative', zIndex: 1 }}>{children}</div>
    </div>
  );
}

function PanelTitle({ children, icon: Icon, subtitle }) {
  return (
    <div style={{ marginBottom: subtitle ? 10 : 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        {Icon ? (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 34,
              height: 34,
              borderRadius: 12,
              background: 'linear-gradient(145deg, rgba(255,255,255,0.85), rgba(237,233,254,0.55))',
              color: ACCENT_TEXT,
              boxShadow: `${GLOSS_SHINE}, 0 4px 12px rgba(99,102,241,0.1)`,
              border: `1px solid ${GLASS_BORDER}`,
            }}
          >
            <Icon size={17} />
          </span>
        ) : null}
        <div style={{ fontSize: 16, fontWeight: 600, color: TEXT, letterSpacing: '-0.015em', lineHeight: 1.3 }}>
          {children}
        </div>
      </div>
      {subtitle ? (
        <div style={{ fontSize: 13, color: MUTED, marginTop: 6, lineHeight: 1.55, paddingLeft: Icon ? 44 : 0 }}>
          {subtitle}
        </div>
      ) : null}
    </div>
  );
}

function StatusChip({ children, tone = 'default' }) {
  const tones = {
    default: { bg: 'linear-gradient(180deg, rgba(255,255,255,0.92), rgba(237,233,254,0.65))', color: ACCENT_TEXT, border: GLASS_BORDER },
    ok: { bg: 'linear-gradient(180deg, rgba(255,255,255,0.92), rgba(209,250,229,0.65))', color: OK, border: 'rgba(255,255,255,0.78)' },
    warn: { bg: 'linear-gradient(180deg, rgba(255,255,255,0.92), rgba(254,243,199,0.65))', color: WARN, border: 'rgba(255,255,255,0.78)' },
    muted: { bg: 'linear-gradient(180deg, rgba(255,255,255,0.88), rgba(241,245,249,0.62))', color: MUTED, border: GLASS_BORDER },
  };
  const t = tones[tone] || tones.default;
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '5px 11px',
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: '0.015em',
        background: t.bg,
        color: t.color,
        border: `1px solid ${t.border}`,
        whiteSpace: 'nowrap',
        boxShadow: GLOSS_SHINE,
        backdropFilter: 'blur(8px)',
      }}
    >
      {children}
    </span>
  );
}

function StatCard({ icon: Icon, label, value, hint, tone = 'default' }) {
  const colors = {
    default: { bg: ACCENT_SOFT, color: ACCENT },
    ok: { bg: 'rgba(34,197,94,0.14)', color: OK },
    warn: { bg: 'rgba(251,191,36,0.22)', color: WARN },
    danger: { bg: 'rgba(239,68,68,0.12)', color: DANGER },
  };
  const c = colors[tone] || colors.default;
  return (
    <div className="backup-stat-tile" style={{ minHeight: 112 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
        <span
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 44,
            height: 44,
            borderRadius: 14,
            background: `linear-gradient(155deg, rgba(255,255,255,0.95), ${c.bg})`,
            color: c.color,
            flexShrink: 0,
            boxShadow: `${GLOSS_SHINE}, 0 6px 16px rgba(99,102,241,0.08)`,
            border: `1px solid ${GLASS_BORDER}`,
          }}
        >
          {Icon ? <Icon size={21} /> : null}
        </span>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: MUTED, marginBottom: 6, letterSpacing: '0.04em' }}>{label}</div>
          <div style={{ fontSize: 22, fontWeight: 600, color: TEXT, lineHeight: 1.2, letterSpacing: '-0.02em' }}>{value}</div>
          {hint ? <div style={{ fontSize: 12, color: BODY, marginTop: 7, lineHeight: 1.45, fontWeight: 500 }}>{hint}</div> : null}
        </div>
      </div>
    </div>
  );
}

function ActionBtn({ children, onClick, disabled, variant = 'primary', icon: Icon }) {
  const styles = {
    primary: {
      background: `linear-gradient(135deg, ${ACCENT_2} 0%, ${ACCENT} 55%, #7c3aed 100%)`,
      color: '#ffffff',
      border: '1px solid rgba(255, 255, 255, 0.35)',
      boxShadow: '0 10px 28px rgba(99,102,241,0.32), inset 0 1px 0 rgba(255,255,255,0.35), inset 0 -1px 0 rgba(67,56,202,0.2)',
    },
    danger: {
      background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(254,226,226,0.88) 55%, rgba(252,165,165,0.55) 100%)',
      color: DANGER,
      border: '1px solid rgba(255, 255, 255, 0.75)',
      boxShadow: '0 6px 20px rgba(239,68,68,0.12), inset 0 1px 0 rgba(255,255,255,0.95), inset 0 -1px 0 rgba(254,202,202,0.4)',
    },
    ghost: {
      background: 'linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(238,242,255,0.88) 100%)',
      color: ACCENT_TEXT,
      border: '1px solid rgba(255, 255, 255, 0.82)',
      boxShadow: '0 6px 18px rgba(99,102,241,0.1), inset 0 1px 0 rgba(255,255,255,0.98), inset 0 -1px 0 rgba(199,210,254,0.35)',
    },
    success: {
      background: 'linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(220,252,231,0.88) 55%, rgba(167,243,208,0.55) 100%)',
      color: OK,
      border: '1px solid rgba(255, 255, 255, 0.78)',
      boxShadow: '0 6px 20px rgba(16,185,129,0.12), inset 0 1px 0 rgba(255,255,255,0.95), inset 0 -1px 0 rgba(167,243,208,0.45)',
    },
  };
  const s = styles[variant] || styles.primary;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`backup-action-btn backup-action-${variant}`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        padding: '12px 20px',
        borderRadius: 14,
        cursor: disabled ? 'not-allowed' : 'pointer',
        fontWeight: 600,
        fontSize: 14,
        fontFamily: FONT,
        opacity: disabled ? 0.55 : 1,
        letterSpacing: '0.005em',
        transition: 'transform 0.18s ease, box-shadow 0.18s ease, opacity 0.18s ease',
        ...s,
      }}
    >
      {Icon ? <Icon size={16} /> : null}
      {children}
    </button>
  );
}

function MeterBar({ label, usedPercent, freeGb, sizeGb, accent }) {
  const v = Number.isFinite(usedPercent) ? Math.min(100, Math.max(0, usedPercent)) : null;
  const hot = v != null && v >= 85;
  const warn = v != null && v >= 70 && v < 85;
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: STRONG }}>{label}</span>
        <span style={{ fontSize: 12, color: MUTED }}>
          {v != null ? `${v}% занято` : '—'}
          {freeGb != null ? ` · свободно ${formatGb(freeGb)}` : ''}
          {sizeGb != null ? ` · всего ${formatGb(sizeGb)}` : ''}
        </span>
      </div>
      <div style={{ height: 11, borderRadius: 999, background: 'linear-gradient(180deg, rgba(255,255,255,0.75), rgba(226,232,240,0.45))', overflow: 'hidden', boxShadow: 'inset 0 1px 2px rgba(15,23,42,0.05), inset 0 -1px 0 rgba(255,255,255,0.6)' }}>
        <div
          style={{
            width: v != null ? `${v}%` : 0,
            height: '100%',
            borderRadius: 999,
            background: hot
              ? 'linear-gradient(90deg, #f87171, #fb923c)'
              : warn
                ? 'linear-gradient(90deg, #fbbf24, #f97316)'
                : accent || `linear-gradient(90deg, ${ACCENT_2}, ${ACCENT})`,
            transition: 'width 0.35s ease',
            boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.35)',
          }}
        />
      </div>
    </div>
  );
}

function DriveCard({ volume, selected, disabled, onSelect }) {
  const hot = volume.usedPercent >= 85;
  const warn = volume.usedPercent >= 70 && volume.usedPercent < 85;
  const blocked = volume.isSystemDrive || volume.fileSystem !== 'NTFS';
  return (
    <button
      type="button"
      onClick={() => !disabled && !blocked && onSelect(volume.drive)}
      disabled={disabled || blocked}
      style={{
        textAlign: 'left',
        padding: 16,
        borderRadius: 16,
        border: selected
          ? `1px solid rgba(255, 255, 255, 0.9)`
          : `1px solid ${GLASS_BORDER}`,
        background: selected
          ? 'linear-gradient(165deg, rgba(255,255,255,0.96), rgba(237,233,254,0.72))'
          : GLOSS_SURFACE,
        cursor: disabled || blocked ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : blocked ? 0.45 : 1,
        width: '100%',
        color: TEXT,
        boxShadow: selected
          ? '0 12px 32px rgba(99,102,241,0.14), inset 0 1px 0 rgba(255,255,255,0.98), inset 0 -1px 0 rgba(255,255,255,0.3)'
          : `${GLOSS_FLOAT}, ${GLOSS_SHINE}`,
        fontFamily: FONT,
        transition: 'border-color 0.2s, box-shadow 0.2s, background 0.2s, transform 0.2s',
        backdropFilter: 'blur(12px)',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 600, letterSpacing: '-0.02em' }}>{volume.drive}</div>
          <div style={{ fontSize: 12, color: MUTED, marginTop: 2 }}>
            {volume.label || 'Без метки'} · {volume.fileSystem || '—'}
          </div>
        </div>
        {selected ? (
          <span style={{ fontSize: 11, fontWeight: 600, color: ACCENT_TEXT, background: 'linear-gradient(180deg, rgba(255,255,255,0.92), rgba(237,233,254,0.65))', padding: '4px 8px', borderRadius: 999, border: `1px solid ${GLASS_BORDER}`, boxShadow: GLOSS_SHINE }}>
            Выбран
          </span>
        ) : null}
        {volume.isSystemDrive ? (
          <span style={{ fontSize: 11, color: DANGER }}>Системный</span>
        ) : null}
      </div>
      <div style={{ marginTop: 12 }}>
        <MeterBar
          label=""
          usedPercent={volume.usedPercent}
          freeGb={volume.freeGb}
          sizeGb={volume.sizeGb}
          accent={hot ? undefined : warn ? 'linear-gradient(90deg, #fbbf24, #f97316)' : undefined}
        />
      </div>
      <div style={{ fontSize: 11, color: MUTED, marginTop: 4 }}>
        {volume.healthStatus ? `Состояние: ${volume.healthStatus}` : ''}
        {hot ? ' · мало места' : ''}
      </div>
    </button>
  );
}

export default function BackupControlModal({ open, onClose, isMobile = false, fullViewport = false }) {
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState(null);
  const [tab, setTab] = useState('overview');
  const [helpOpen, setHelpOpen] = useState(false);
  const [draftDrive, setDraftDrive] = useState('D:');
  const [draftHour, setDraftHour] = useState(3);
  const [draftMinute, setDraftMinute] = useState(0);
  const [draftRetentionEnabled, setDraftRetentionEnabled] = useState(true);
  const [draftKeepCount, setDraftKeepCount] = useState(5);
  const [draftMaxAgeDays, setDraftMaxAgeDays] = useState(0);
  const [draftCleanupHour, setDraftCleanupHour] = useState(6);
  const [draftCleanupMinute, setDraftCleanupMinute] = useState(0);
  const [draftAccountsEnabled, setDraftAccountsEnabled] = useState(true);
  const [draftAccountsHour, setDraftAccountsHour] = useState(4);
  const [draftAccountsMinute, setDraftAccountsMinute] = useState(30);
  const [draftAccountsKeep, setDraftAccountsKeep] = useState(30);
  const [cleanupPreview, setCleanupPreview] = useState(null);
  const [diskVolumes, setDiskVolumes] = useState([]);
  const [volumesLoading, setVolumesLoading] = useState(false);
  const [volumesError, setVolumesError] = useState('');
  const runningRef = useRef(false);
  const pollStoppedRef = useRef(false);
  const draftTouchedRef = useRef(false);

  const apiFetch = useCallback(async (path, options = {}) => {
    const token = localStorage.getItem('token');
    if (!token) throw new Error('Нет токена авторизации');
    const r = await fetch(`/api/admin/server-backup${path}`, {
      ...options,
      headers: {
        Authorization: `Bearer ${token}`,
        ...(options.body ? { 'Content-Type': 'application/json' } : {}),
        ...(options.headers || {}),
      },
    });
    let data = {};
    try {
      data = await r.json();
    } catch {
      /* noop */
    }
    if (!r.ok) {
      const msg = data.error || data.message || `Ошибка ${r.status}`;
      if (r.status === 504) {
        throw new Error('504: сервер не ответил вовремя. Закройте лишние окна «Архивация данных» на сервере и установите компонент Windows Server Backup.');
      }
      const err = new Error(msg);
      err.validation = data.validation;
      err.status = r.status;
      err.hint = data.hint;
      err.apiCode = data.code;
      throw err;
    }
    return data;
  }, []);

  const loadVolumes = useCallback(async () => {
    setVolumesLoading(true);
    setVolumesError('');
    try {
      const data = await apiFetch('/volumes');
      const list = data.volumes || [];
      setDiskVolumes(list);
      setStatus((prev) => (prev ? { ...prev, volumes: list, volumesLoaded: true, volumesError: null } : prev));
    } catch (e) {
      const msg = e.message || 'Не удалось загрузить диски';
      setVolumesError(msg);
      setStatus((prev) => (prev ? { ...prev, volumesLoaded: true, volumesError: msg } : prev));
    } finally {
      setVolumesLoading(false);
    }
  }, [apiFetch]);

  const loadStatus = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setError('');
    try {
      const data = await apiFetch('/status');
      setStatus(data);
      if (Array.isArray(data.volumes) && data.volumes.length > 0) {
        setDiskVolumes(data.volumes);
      }
      runningRef.current = !!data.running;
      pollStoppedRef.current = data.wsbInstalled === false;
      if (data.config && !silent && !draftTouchedRef.current) {
        setDraftDrive(data.config.targetDrive || 'D:');
        setDraftHour(data.config.scheduleHour ?? 3);
        setDraftMinute(data.config.scheduleMinute ?? 0);
        setDraftRetentionEnabled(data.config.retentionEnabled !== false);
        setDraftKeepCount(data.config.retentionKeepCount ?? 5);
        setDraftMaxAgeDays(data.config.retentionMaxAgeDays ?? 0);
        setDraftCleanupHour(data.config.cleanupHour ?? 6);
        setDraftCleanupMinute(data.config.cleanupMinute ?? 0);
        setDraftAccountsEnabled(data.config.accountsBackupEnabled !== false);
        setDraftAccountsHour(data.config.accountsScheduleHour ?? 4);
        setDraftAccountsMinute(data.config.accountsScheduleMinute ?? 30);
        setDraftAccountsKeep(data.config.accountsRetentionKeepCount ?? 30);
      }
    } catch (e) {
      const msg = e.message || 'Не удалось загрузить статус';
      setError(msg);
      if (/504/.test(msg)) pollStoppedRef.current = true;
    } finally {
      if (!silent) setLoading(false);
    }
  }, [apiFetch]);

  useEffect(() => {
    if (!open) return undefined;
    pollStoppedRef.current = false;
    draftTouchedRef.current = false;
    loadStatus();
    loadVolumes();
    const id = setInterval(() => {
      if (pollStoppedRef.current) return;
      loadStatus(true);
    }, 8000);
    return () => clearInterval(id);
  }, [open, loadStatus, loadVolumes]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (status?.running !== undefined) {
      runningRef.current = !!status.running;
    }
  }, [status?.running]);

  useEffect(() => {
    if (!open) {
      setMessage('');
      setError('');
      setTab('overview');
    }
  }, [open]);

  const runAction = async (key, path, body, successMsg) => {
    setActionLoading(key);
    setError('');
    setMessage('');
    try {
      const data = await apiFetch(path, {
        method: 'POST',
        body: body != null ? JSON.stringify(body) : '{}',
      });
      setMessage(data.message || successMsg);
      await loadStatus(true);
    } catch (e) {
      const parts = [e.message || 'Ошибка операции'];
      if (e.hint) parts.push(String(e.hint));
      setError(parts.filter(Boolean).join('\n\n'));
    } finally {
      setActionLoading('');
    }
  };

  const installBackupService = async () => {
    setActionLoading('install');
    setError('');
    setMessage('');
    try {
      const drive = draftDrive || status?.config?.targetDrive || 'D:';
      const data = await apiFetch('/install-tasks', {
        method: 'POST',
        body: JSON.stringify({ targetDrive: drive }),
      });
      setMessage(data.message || 'Служба бэкапа установлена');
      draftTouchedRef.current = false;
      await loadStatus(true);
    } catch (e) {
      const parts = [e.message || 'Ошибка установки'];
      if (e.hint) parts.push(String(e.hint));
      setError(parts.filter(Boolean).join('\n\n'));
    } finally {
      setActionLoading('');
    }
  };

  const saveSettings = async (apply) => {
    setActionLoading(apply ? 'apply' : 'save');
    setError('');
    setMessage('');
    try {
      const data = await apiFetch('/settings', {
        method: 'PUT',
        body: JSON.stringify({
          targetDrive: draftDrive,
          scheduleHour: Number(draftHour),
          scheduleMinute: Number(draftMinute),
          retentionEnabled: draftRetentionEnabled,
          retentionKeepCount: Number(draftKeepCount),
          retentionMaxAgeDays: Number(draftMaxAgeDays),
          cleanupHour: Number(draftCleanupHour),
          cleanupMinute: Number(draftCleanupMinute),
          accountsBackupEnabled: draftAccountsEnabled,
          accountsScheduleHour: Number(draftAccountsHour),
          accountsScheduleMinute: Number(draftAccountsMinute),
          accountsRetentionKeepCount: Number(draftAccountsKeep),
          apply: !!apply,
        }),
      });
      setMessage(data.message || (apply ? 'Настройки применены' : 'Сохранено'));
      draftTouchedRef.current = false;
      await loadStatus(true);
    } catch (e) {
      const parts = [e.message || 'Ошибка сохранения'];
      if (e.validation?.errors?.length) parts.unshift(...e.validation.errors);
      else if (e.validation?.warnings?.length) parts.push(...e.validation.warnings);
      setError(parts.filter(Boolean).join(' '));
    } finally {
      setActionLoading('');
    }
  };

  const runCleanupPreview = async () => {
    setActionLoading('preview');
    setError('');
    try {
      await saveSettings(false);
      const data = await apiFetch('/cleanup/preview');
      setCleanupPreview(data);
      setMessage(`К удалению: ${data.toDelete?.length || 0}, останется: ${data.toKeep?.length || 0}`);
    } catch (e) {
      setError(e.message || 'Ошибка просмотра');
    } finally {
      setActionLoading('');
    }
  };

  const runCleanupNow = async (dryRun = false) => {
    setActionLoading(dryRun ? 'previewRun' : 'cleanup');
    setError('');
    setMessage('');
    try {
      if (!dryRun) await saveSettings(false);
      const data = await apiFetch('/cleanup/run', {
        method: 'POST',
        body: JSON.stringify({ dryRun }),
      });
      setMessage(data.message || 'Готово');
      setCleanupPreview(data);
      await loadStatus(true);
    } catch (e) {
      setError(e.message || 'Ошибка очистки');
    } finally {
      setActionLoading('');
    }
  };

  const targetDrive = status?.config?.targetDrive || draftDrive || 'D:';
  const scheduleLabel = status?.scheduleTime || `${pad2(draftHour)}:${pad2(draftMinute)}`;

  const historyRuns = useMemo(() => {
    if (Array.isArray(status?.backupHistory) && status.backupHistory.length > 0) {
      return status.backupHistory;
    }
    if (Array.isArray(status?.versions) && status.versions.length > 0) {
      return status.versions.map((v) => ({
        startedAt: v.backupTime || v.version,
        finishedAt: v.backupTime || null,
        result: v.canRecover !== false ? 'Succeeded' : 'Failed',
        success: v.canRecover !== false,
        durationSeconds: v.durationSeconds ?? null,
        version: v.version,
      }));
    }
    return [];
  }, [status?.backupHistory, status?.versions]);

  const restoreVersions = useMemo(() => {
    if (Array.isArray(status?.versions) && status.versions.length > 0) return status.versions;
    return historyRuns
      .filter((r) => r.success && r.finishedAt)
      .map((r) => ({
        version: r.version || r.startedAt,
        backupTime: r.finishedAt,
        canRecover: true,
        durationSeconds: r.durationSeconds,
      }));
  }, [status?.versions, historyRuns]);

  const lastResultTone = useMemo(() => {
    const r = String(status?.lastResult || '').toLowerCase();
    if (!r) return 'default';
    if (r.includes('success') || r.includes('completed') || r === '0') return 'ok';
    if (r.includes('fail') || r.includes('error')) return 'danger';
    if (r.includes('running') || r.includes('progress')) return 'warn';
    return 'default';
  }, [status?.lastResult]);

  const allWarnings = useMemo(() => {
    const list = [...(status?.warnings || [])];
    if (status?.configMatchesPolicy === false) {
      list.push(`Windows использует ${status.activeTargetDrive}, в настройках — ${targetDrive}.`);
    }
    if (status?.running) {
      list.push('Идёт бэкап — смена диска недоступна до завершения или остановки.');
    }
    if (status?.windows && status?.processElevated === false) {
      list.push('Node.js запущен без прав администратора — установка бэкапа из UI не сработает.');
    }
    return [...new Set(list)];
  }, [status, targetDrive]);

  const volumes = useMemo(() => {
    const fromStatus = status?.volumes;
    if (Array.isArray(fromStatus) && fromStatus.length > 0) return fromStatus;
    if (diskVolumes.length > 0) return diskVolumes;
    return fromStatus || diskVolumes || [];
  }, [status?.volumes, diskVolumes]);

  const volumesReady = !!(status?.volumesLoaded || (status && !loading && !volumesLoading));
  const volumesLoadError = volumesError || status?.volumesError || '';
  const needsFirstSetup =
    status?.windows && status?.wsbInstalled !== false && !status?.policyConfigured && !status?.running;
  const setupLogTail = status?.logs?.setup || '';
  const setupLastError = (() => {
    const lines = String(setupLogTail).split(/\r?\n/).filter(Boolean);
    for (let i = lines.length - 1; i >= 0; i -= 1) {
      if (/ERROR:/i.test(lines[i])) return lines[i].replace(/^.*ERROR:\s*/i, '').trim();
    }
    return '';
  })();
  const policyMismatch = status?.configMatchesPolicy === false;
  const tasksInstalled = status?.backupTasks?.installed === true;
  const processElevated = status?.processElevated === true;
  const apiSchedulerInstalled = status?.apiScheduler?.installed === true;
  const accountsInfo = status?.accountsBackup;
  const accountsScheduleLabel = `${pad2(draftAccountsHour)}:${pad2(draftAccountsMinute)}`;

  if (!open) return null;

  const shellStyle = fullViewport
    ? {
        position: 'fixed',
        inset: 0,
        zIndex: 500001,
        display: 'flex',
        flexDirection: 'column',
        background: SHELL_BG,
        color: TEXT,
        overflow: 'hidden',
        fontFamily: FONT,
        WebkitFontSmoothing: 'antialiased',
      }
    : {
        position: 'fixed',
        inset: isMobile ? 0 : '4vh 6vw',
        zIndex: 500001,
        display: 'flex',
        flexDirection: 'column',
        borderRadius: isMobile ? 0 : 24,
        background: SHELL_BG,
        color: TEXT,
        overflow: 'hidden',
        fontFamily: FONT,
        WebkitFontSmoothing: 'antialiased',
        boxShadow: '0 32px 80px rgba(99, 102, 241, 0.14), 0 8px 24px rgba(15, 23, 42, 0.06)',
        border: `1px solid ${GLASS_BORDER}`,
      };

  return (
    <div style={shellStyle} className="backup-modal-shell">
      <div
        aria-hidden
        style={{
          position: 'absolute',
          top: -80,
          right: -40,
          width: 280,
          height: 280,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(139,92,246,0.18) 0%, transparent 70%)',
          pointerEvents: 'none',
        }}
      />
      <div
        aria-hidden
        style={{
          position: 'absolute',
          bottom: -60,
          left: -30,
          width: 240,
          height: 240,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(99,102,241,0.14) 0%, transparent 70%)',
          pointerEvents: 'none',
        }}
      />
      <div
        style={{
          height: 4,
          flexShrink: 0,
          background: `linear-gradient(90deg, ${ACCENT}, ${ACCENT_2}, #a78bfa)`,
        }}
      />
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 16,
          padding: isMobile ? '16px 16px 12px' : '20px 24px 16px',
          borderBottom: `1px solid rgba(255, 255, 255, 0.55)`,
          flexShrink: 0,
          background: 'linear-gradient(180deg, rgba(255,255,255,0.82) 0%, rgba(248,250,255,0.45) 100%)',
          backdropFilter: 'blur(16px) saturate(1.3)',
          position: 'relative',
          zIndex: 1,
        }}
      >
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 46,
                height: 46,
                borderRadius: 14,
                background: `linear-gradient(135deg, ${ACCENT_2}, ${ACCENT})`,
                color: '#fff',
                boxShadow: '0 8px 20px rgba(99,102,241,0.35), inset 0 1px 0 rgba(255,255,255,0.3)',
              }}
            >
              <FiHardDrive size={22} />
            </span>
            <div>
              <h2 style={{ margin: 0, fontSize: isMobile ? 20 : 24, fontWeight: 600, letterSpacing: '-0.02em', color: TEXT, lineHeight: 1.2 }}>
                Бэкап сервера и учёток
              </h2>
              <div style={{ fontSize: 13, color: BODY, marginTop: 4, lineHeight: 1.4 }}>
                {status?.host ? `Сервер ${status.host}` : 'Windows Server Backup'}
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 12 }}>
            {scheduleLabel ? <StatusChip tone="default">Образ C: {scheduleLabel}</StatusChip> : null}
            {draftAccountsEnabled ? <StatusChip tone="muted">Учётки {accountsScheduleLabel}</StatusChip> : null}
            {tasksInstalled ? <StatusChip tone="ok">Служба OK</StatusChip> : <StatusChip tone="warn">Нужна установка</StatusChip>}
            {status?.policyConfigured ? <StatusChip tone="ok">Windows настроен</StatusChip> : status?.windows ? <StatusChip tone="warn">Первичная настройка</StatusChip> : null}
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Закрыть"
          className="backup-close-btn"
          style={{
            width: 44,
            height: 44,
            borderRadius: 14,
            border: `1px solid rgba(255, 255, 255, 0.78)`,
            background: 'linear-gradient(180deg, rgba(255,255,255,0.96) 0%, rgba(254,242,242,0.75) 100%)',
            color: DANGER,
            cursor: 'pointer',
            fontSize: 22,
            fontWeight: 600,
            boxShadow: '0 6px 18px rgba(239,68,68,0.1), inset 0 1px 0 rgba(255,255,255,0.98), inset 0 -1px 0 rgba(254,202,202,0.35)',
            flexShrink: 0,
          }}
        >
          ×
        </button>
      </div>

      <div
        style={{
          padding: isMobile ? '12px 16px' : '14px 24px 16px',
          borderBottom: `1px solid rgba(255, 255, 255, 0.45)`,
          flexShrink: 0,
          position: 'relative',
          zIndex: 1,
        }}
      >
        <div
          className="backup-tab-bar"
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 6,
            padding: 6,
            borderRadius: 18,
            background: 'linear-gradient(180deg, rgba(255,255,255,0.72) 0%, rgba(241,245,255,0.48) 100%)',
            border: `1px solid ${GLASS_BORDER}`,
            boxShadow: `${GLOSS_SHINE}, 0 8px 24px rgba(99, 102, 241, 0.07)`,
            backdropFilter: 'blur(14px) saturate(1.25)',
          }}
        >
        {[
          { id: 'overview', label: 'Обзор', icon: FiActivity },
          {
            id: 'settings',
            label: needsFirstSetup || policyMismatch ? 'Настройки ⚠' : 'Настройки',
            icon: FiSettings,
          },
          { id: 'accounts', label: 'Учётки', icon: FiUsers },
          { id: 'history', label: 'История', icon: FiList },
          { id: 'logs', label: 'Логи', icon: FiDatabase },
        ].map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={tab === t.id ? 'backup-tab-btn backup-tab-btn-active' : 'backup-tab-btn'}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              padding: '10px 16px',
              borderRadius: 14,
              border: tab === t.id ? `1px solid ${GLASS_BORDER}` : '1px solid transparent',
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: 13,
              fontFamily: FONT,
              background: tab === t.id
                ? 'linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(237,233,254,0.72) 100%)'
                : 'transparent',
              color: tab === t.id ? ACCENT_TEXT : MUTED,
              boxShadow: tab === t.id
                ? `${GLOSS_SHINE}, 0 6px 18px rgba(99,102,241,0.12)`
                : 'none',
              transition: 'all 0.2s ease',
            }}
          >
            <t.icon size={15} />
            {t.label}
          </button>
        ))}
        </div>
      </div>

      <div style={{ flex: 1, overflow: 'auto', padding: isMobile ? 16 : 24, position: 'relative', zIndex: 1 }}>
        {loading && !status ? (
          <GlassPanel style={{ marginBottom: 16 }}>
            <div style={{ color: MUTED, fontSize: 14 }}>Загрузка статуса сервера…</div>
          </GlassPanel>
        ) : null}

        {status?.windows && status?.wsbInstalled !== false && tab === 'overview' ? (
          <GlassPanel style={{ marginBottom: 16 }} variant="accent">
            <button
              type="button"
              onClick={() => setHelpOpen((v) => !v)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
                padding: 0,
                fontFamily: FONT,
                marginBottom: helpOpen ? 12 : 0,
              }}
            >
              <PanelTitle icon={FiInfo} subtitle={helpOpen ? 'Краткая инструкция' : 'Нажмите, чтобы развернуть'}>
                Как пользоваться
              </PanelTitle>
              <span style={{ fontSize: 13, fontWeight: 600, color: ACCENT_TEXT, flexShrink: 0, marginLeft: 12 }}>
                {helpOpen ? 'Свернуть' : 'Показать'}
              </span>
            </button>
            {helpOpen ? (
            <ol style={{ margin: 0, paddingLeft: 20, fontSize: 14, color: BODY, lineHeight: 1.7 }}>
              <li>
                Один раз запустите <strong style={{ color: OK }}>Install-MesendgerApiScheduler.bat</strong> от администратора
                (Node с правами админа) → затем <strong style={{ color: OK }}>«Установить службу бэкапа»</strong>.
              </li>
              <li>
                <strong style={{ color: STRONG }}>Настройки</strong> — диск, расписание образа C: и учёток →{' '}
                <strong style={{ color: OK }}>«Сохранить и применить в Windows»</strong>.
              </li>
              <li>
                <strong style={{ color: STRONG }}>Обзор / Учётки</strong> — ручной запуск бэкапа сервера или учётных записей.
              </li>
              <li>
                <strong style={{ color: STRONG }}>История / Логи</strong> — точки восстановления и журнал операций.
              </li>
            </ol>
            ) : null}
          </GlassPanel>
        ) : null}

        {status?.windows && !processElevated ? (
          <GlassPanel style={{ marginBottom: 16 }} variant="warn">
            <div style={{ fontWeight: 600, color: '#b45309', fontSize: 15, marginBottom: 8 }}>
              Node.js без прав администратора
            </div>
            <p style={{ margin: '0 0 12px', fontSize: 13, color: MUTED, lineHeight: 1.55 }}>
              Задачи <strong style={{ color: '#334155' }}>MesendgerBackup_*</strong> в планировщике — это только скрипты бэкапа (SYSTEM),
              они <strong style={{ color: '#dc2626' }}>не запускают</strong> мессенджер. Node вы запускаете отдельно — и он сейчас
              работает как обычный пользователь{apiSchedulerInstalled ? '' : ', автозапуск с админ-правами не настроен'}.
            </p>
            <p style={{ margin: '0 0 12px', fontSize: 13, color: MUTED, lineHeight: 1.55 }}>
              <strong style={{ color: '#15803d' }}>Решение (один раз):</strong> правый клик → «Запуск от имени администратора» на файле:
            </p>
            <pre
              style={{
                margin: '0 0 12px',
                padding: '10px 12px',
                borderRadius: 10,
                background: INPUT_BG,
                border: `1px solid ${GLASS_BORDER}`,
                color: '#334155',
                fontSize: 12,
                lineHeight: 1.5,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-all',
              }}
            >
              C:\Users\Ksendz\web\mesendger-god\mesendger\telegram-clone\Install-MesendgerApiScheduler.bat
            </pre>
            <p style={{ margin: 0, fontSize: 12, color: MUTED, lineHeight: 1.55 }}>
              Создаст задачу <strong style={{ color: '#334155' }}>Mesendger_OfficeServer</strong> (RunLevel Highest) — при загрузке Windows
              и входе в систему Node стартует с правами администратора. После установки перезапустите API или выполните:{' '}
              <code style={{ color: '#334155' }}>schtasks /Run /TN Mesendger_OfficeServer</code>
            </p>
          </GlassPanel>
        ) : null}

        {status?.windows && !tasksInstalled ? (
          <GlassPanel style={{ marginBottom: 16 }} variant="danger">
            <div style={{ fontWeight: 600, color: '#dc2626', fontSize: 15, marginBottom: 8 }}>
              Служба бэкапа не установлена
            </div>
            <p style={{ margin: '0 0 12px', fontSize: 13, color: MUTED, lineHeight: 1.55 }}>
              {processElevated
                ? 'Node работает с правами администратора — можно нажать кнопку ниже.'
                : 'Сначала выполните Install-MesendgerApiScheduler.bat (см. жёлтый блок выше), затем кнопку ниже или PowerShell:'}
              {' '}(диск: <strong style={{ color: '#334155' }}>{targetDrive}</strong>)
            </p>
            <pre
              style={{
                margin: '0 0 12px',
                padding: '10px 12px',
                borderRadius: 10,
                background: INPUT_BG,
                border: `1px solid ${GLASS_BORDER}`,
                color: '#334155',
                fontSize: 12,
                lineHeight: 1.5,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-all',
              }}
            >
              {`powershell -NoProfile -ExecutionPolicy Bypass -File "C:\\Users\\Ksendz\\web\\mesendger-god\\mesendger\\telegram-clone\\server\\scripts\\MAINSERVER-Backup\\Install-BackupTasks.ps1" -TargetVolume ${targetDrive}`}
            </pre>
            <ActionBtn
              icon={FiShield}
              onClick={installBackupService}
              disabled={!!actionLoading}
              variant="ghost"
            >
              {actionLoading === 'install' ? 'Проверка…' : 'Повторить установку (если Node — от админа)'}
            </ActionBtn>
          </GlassPanel>
        ) : null}

        {needsFirstSetup ? (
          <GlassPanel style={{ marginBottom: 16 }} variant="warn">
            <div style={{ fontWeight: 600, color: '#b45309', fontSize: 15, marginBottom: 8 }}>
              Первичная настройка не выполнена
            </div>
            <p style={{ margin: '0 0 12px', fontSize: 13, color: MUTED, lineHeight: 1.55 }}>
              Windows Server Backup ещё не настроен (C: → {targetDrive}). Запустите один раз от администратора файл{' '}
              <strong style={{ color: '#334155' }}>RUN-FirstSetup-Admin.cmd</strong> в папке telegram-clone
              или на вкладке «Настройки» нажмите «Сохранить и применить в Windows» (нужен Node от админа).
            </p>
            {setupLastError ? (
              <p style={{ margin: '0 0 12px', fontSize: 12, color: DANGER, lineHeight: 1.5 }}>
                Последняя ошибка: {setupLastError}
              </p>
            ) : null}
            <ActionBtn icon={FiSettings} onClick={() => setTab('settings')} variant="success">
              Перейти в настройки
            </ActionBtn>
          </GlassPanel>
        ) : null}

        {policyMismatch ? (
          <GlassPanel style={{ marginBottom: 16 }} variant="warn">
            <div style={{ fontWeight: 600, color: '#b45309', marginBottom: 6 }}>Диск в Windows не совпадает с настройками</div>
            <p style={{ margin: '0 0 10px', fontSize: 13, color: MUTED, lineHeight: 1.5 }}>
              В Windows сейчас: <strong>{status?.activeTargetDrive || '—'}</strong>, в приложении: <strong>{targetDrive}</strong>.
              Нажмите «Сохранить и применить» на вкладке «Настройки».
            </p>
            <ActionBtn icon={FiSettings} onClick={() => setTab('settings')} variant="ghost">
              Исправить в настройках
            </ActionBtn>
          </GlassPanel>
        ) : null}

        {!status?.windows && status && (
          <GlassPanel style={{ marginBottom: 16 }} variant="warn">
            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
              <FiAlertTriangle color="#fbbf24" size={20} />
              <div style={{ fontSize: 13, color: MUTED, lineHeight: 1.5 }}>
                {status.error || 'Доступно только на Windows-сервере с Windows Server Backup.'}
              </div>
            </div>
          </GlassPanel>
        )}

        {status?.windows && status?.wsbInstalled === false && (
          <GlassPanel style={{ marginBottom: 16 }} variant="danger">
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
              <FiAlertTriangle color="#f87171" size={22} style={{ flexShrink: 0, marginTop: 2 }} />
              <div>
                <div style={{ fontWeight: 600, color: '#dc2626', fontSize: 16, marginBottom: 8 }}>
                  Windows Server Backup не установлен
                </div>
                <div style={{ fontSize: 13, color: MUTED, lineHeight: 1.6 }}>
                  {status.wsbInstallHint || 'Установите компонент через Server Manager.'}
                </div>
                <div style={{ fontSize: 13, color: '#b45309', marginTop: 10, lineHeight: 1.55 }}>
                  Пока компонент не установлен, модуль не вызывает wbadmin (чтобы не открывались окна архивации).
                  После установки нажмите «Обновить» или «Сохранить и применить» на вкладке «Настройки».
                </div>
              </div>
            </div>
          </GlassPanel>
        )}

        {allWarnings.length > 0 && (
          <GlassPanel style={{ marginBottom: 16 }} variant="warn">
            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
              <FiShield color="#fbbf24" size={20} style={{ flexShrink: 0, marginTop: 2 }} />
              <div>
                <div style={{ fontWeight: 600, color: '#b45309', marginBottom: 6 }}>Обратите внимание</div>
                {allWarnings.map((w) => (
                  <div key={w} style={{ fontSize: 13, color: MUTED, lineHeight: 1.5, marginBottom: 4 }}>• {w}</div>
                ))}
              </div>
            </div>
          </GlassPanel>
        )}

        {error ? (
          <GlassPanel style={{ marginBottom: 16 }} variant="danger">
            <div style={{ color: '#dc2626', fontSize: 14 }}>{error}</div>
          </GlassPanel>
        ) : null}

        {message ? (
          <GlassPanel style={{ marginBottom: 16 }} variant="success">
            <div style={{ color: '#15803d', fontSize: 14 }}>{message}</div>
          </GlassPanel>
        ) : null}

        {status?.running ? (
          <GlassPanel style={{ marginBottom: 16 }} variant="accent">
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <FiRefreshCw size={20} color={ACCENT} className="backup-spin" />
              <div>
                <div style={{ fontWeight: 600, color: '#5b21b6' }}>Бэкап выполняется…</div>
                <div style={{ fontSize: 13, color: MUTED, marginTop: 4 }}>
                  Прошло: {formatDuration(status.elapsedSeconds)}
                  {status.jobState ? ` · ${status.jobState}` : ''}
                  {status.activeTargetDrive ? ` · на ${status.activeTargetDrive}` : ''}
                </div>
              </div>
            </div>
          </GlassPanel>
        ) : null}

        {tab === 'overview' && (
          <>
            <div
              className="backup-stat-grid"
              style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(220px, 1fr))',
                gap: 14,
                marginBottom: 18,
              }}
            >
              <StatCard
                icon={FiCheckCircle}
                label="Последний результат"
                value={formatLastResult(status?.lastResult)}
                hint={formatIsoRu(status?.lastSuccessTime || status?.lastBackupEndTime)}
                tone={lastResultTone}
              />
              <StatCard
                icon={FiList}
                label="Успешных образов"
                value={status?.successCount ?? (status?.backupFolderSizeGb >= 10 ? 1 : '—')}
                hint={`На диске ${targetDrive}`}
              />
              <StatCard
                icon={FiDatabase}
                label="Размер бэкапов"
                value={formatGb(status?.backupFolderSizeGb)}
                hint={status?.backupImageDir}
              />
              <StatCard
                icon={FiClock}
                label="Длительность"
                value={
                  status?.running
                    ? formatDuration(status.elapsedSeconds)
                    : formatDuration(status?.lastBackupDurationSeconds)
                }
                hint={
                  status?.running
                    ? 'Обновление каждые 8 сек'
                    : (status?.lastBackupEndTime
                      ? `Завершён: ${formatIsoRu(status.lastBackupEndTime)}`
                      : 'Последний завершённый бэкап')
                }
              />
              <StatCard
                icon={FiUsers}
                label="Бэкап учёток"
                value={accountsInfo?.latestStats?.users ?? '—'}
                hint={accountsInfo?.latestFile ? `Последний: ${accountsInfo.latestFile.split(/[/\\]/).pop()}` : `${targetDrive}\\MesendgerBackups\\accounts`}
                tone={accountsInfo?.latestFile ? 'ok' : 'default'}
              />
            </div>

            <GlassPanel style={{ marginBottom: 18 }}>
              <PanelTitle icon={FiHardDrive}>Диски</PanelTitle>
              <MeterBar
                label="C: (источник — система)"
                usedPercent={status?.diskSource?.usedPercent}
                freeGb={status?.diskSource?.freeGb}
                sizeGb={status?.diskSource?.sizeGb}
              />
              <MeterBar
                label={`${targetDrive} (назначение — образы)`}
                usedPercent={status?.diskTarget?.usedPercent}
                freeGb={status?.diskTarget?.freeGb}
                sizeGb={status?.diskTarget?.sizeGb}
              />
            </GlassPanel>

            <GlassPanel>
              <PanelTitle icon={FiActivity}>Управление</PanelTitle>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 14 }}>
                <ActionBtn
                  icon={FiPlay}
                  onClick={() => runAction('start', '/start', null, 'Бэкап запущен')}
                  disabled={
                    !!actionLoading ||
                    !status?.windows ||
                    status?.running ||
                    status?.wsbInstalled === false ||
                    !status?.policyConfigured ||
                    !tasksInstalled
                  }
                >
                  {actionLoading === 'start' ? 'Запуск…' : 'Запустить бэкап'}
                </ActionBtn>
                <ActionBtn
                  icon={FiSquare}
                  onClick={() => runAction('stop', '/stop', null, 'Остановлено')}
                  disabled={!!actionLoading || !status?.windows || !status?.running}
                  variant="danger"
                >
                  {actionLoading === 'stop' ? '…' : 'Остановить'}
                </ActionBtn>
                <ActionBtn
                  icon={FiRefreshCw}
                  onClick={() => loadStatus()}
                  disabled={loading}
                  variant="ghost"
                >
                  {loading ? '…' : 'Обновить'}
                </ActionBtn>
                <ActionBtn
                  icon={FiUsers}
                  onClick={() => setTab('accounts')}
                  variant="success"
                >
                  Бэкап учёток
                </ActionBtn>
              </div>
              <div style={{ fontSize: 12, color: MUTED, lineHeight: 1.55 }}>
                <div>• Образ системы: полный диск C: (Windows и программы) → {targetDrive}\WindowsImageBackup\ · учётки: {targetDrive}\MesendgerBackups\accounts\</div>
                <div>• Перед первым запуском — «Установить службу» → «Настройки» → «Сохранить и применить».</div>
                <div>• Бэкап учёток — вкладка «Учётки» или по расписанию из настроек.</div>
              </div>
            </GlassPanel>
          </>
        )}

        {tab === 'accounts' && (
          <>
            <div
              className="backup-stat-grid"
              style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(220px, 1fr))',
                gap: 14,
                marginBottom: 18,
              }}
            >
              <StatCard
                icon={FiUsers}
                label="Пользователей в базе"
                value={accountsInfo?.latestStats?.users ?? '—'}
                hint={accountsInfo?.latestStats?.with_messenger_account != null
                  ? `С учёткой мессенджера: ${accountsInfo.latestStats.with_messenger_account}`
                  : 'Запустите бэкап для актуальных данных'}
              />
              <StatCard
                icon={FiDatabase}
                label="Файлов бэкапа"
                value={accountsInfo?.files?.length ?? 0}
                hint={accountsInfo?.dir || `${targetDrive}\\MesendgerBackups\\accounts`}
              />
              <StatCard
                icon={FiClock}
                label="Расписание"
                value={draftAccountsEnabled ? accountsScheduleLabel : 'Выкл.'}
                hint={draftAccountsEnabled ? 'Ежедневно' : 'Включите в настройках'}
              />
            </div>

            <GlassPanel style={{ marginBottom: 18 }}>
              <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 12 }}>Бэкап учётных записей</div>
              <p style={{ margin: '0 0 14px', fontSize: 13, color: MUTED, lineHeight: 1.55 }}>
                JSON-файл с пользователями мессенджера, структурой компании и паролями Windows/мессенджера
                (из таблицы local_user_passwords). Сохраняется на выбранный диск:{' '}
                <strong style={{ color: '#334155' }}>{targetDrive}\MesendgerBackups\accounts\</strong>
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                <ActionBtn
                  icon={FiPlay}
                  onClick={() => runAction('accounts', '/accounts/export', {}, 'Бэкап учёток выполнен')}
                  disabled={!!actionLoading}
                  variant="success"
                >
                  {actionLoading === 'accounts' ? 'Экспорт…' : 'Сделать бэкап учёток сейчас'}
                </ActionBtn>
                <ActionBtn icon={FiRefreshCw} onClick={() => loadStatus()} disabled={loading} variant="ghost">
                  Обновить
                </ActionBtn>
              </div>
            </GlassPanel>

            <GlassPanel>
              <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 14 }}>Последние файлы</div>
              {!accountsInfo?.files?.length ? (
                <div style={{ color: MUTED, fontSize: 14 }}>Пока нет файлов бэкапа учёток на {targetDrive}.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {accountsInfo.files.map((f) => (
                    <div
                      key={f.name}
                      style={{
                        padding: '12px 14px',
                        borderRadius: 14,
                        border: `1px solid ${GLASS_BORDER}`,
                        background: CODE_BG,
                        boxShadow: `${GLOSS_SHINE}, 0 4px 12px rgba(99,102,241,0.05)`,
                        backdropFilter: 'blur(8px)',
                      }}
                    >
                      <div style={{ fontWeight: 600, color: TEXT, wordBreak: 'break-all' }}>{f.name}</div>
                      <div style={{ fontSize: 12, color: MUTED, marginTop: 4 }}>
                        {formatIsoRu(f.modifiedAt)}
                        {f.sizeBytes ? ` · ${Math.round(f.sizeBytes / 1024)} КБ` : ''}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </GlassPanel>
          </>
        )}

        {tab === 'settings' && (
          <>
            <GlassPanel style={{ marginBottom: 18 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                <FiInfo color={ACCENT} />
                <div style={{ fontSize: 15, fontWeight: 600 }}>Куда сохранять образ</div>
              </div>
              <div style={{ fontSize: 13, color: MUTED, lineHeight: 1.55, marginBottom: 16 }}>
                Выберите диск с достаточным свободным местом (рекомендуется от 200 ГБ). Диск C: недоступен — на нём Windows.
                После смены диска нажмите «Применить» — Windows Server Backup перенастроится автоматически.
              </div>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(240px, 1fr))',
                  gap: 12,
                }}
              >
                {volumes.length > 0
                  ? volumes.map((v) => (
                    <DriveCard
                      key={v.drive}
                      volume={v}
                      selected={draftDrive === v.drive}
                      disabled={!!status?.running || !!actionLoading}
                      onSelect={(drive) => {
                        draftTouchedRef.current = true;
                        setDraftDrive(drive);
                      }}
                    />
                  ))
                  : null}
                {!volumes.length && !volumesReady ? (
                  <div style={{ color: MUTED, fontSize: 14 }}>Список дисков загружается…</div>
                ) : null}
                {!volumes.length && volumesReady ? (
                  <div style={{ gridColumn: isMobile ? '1' : '1 / -1' }}>
                    {volumesLoadError ? (
                      <div style={{ color: '#f87171', fontSize: 14, marginBottom: 12, lineHeight: 1.5 }}>
                        {volumesLoadError}
                      </div>
                    ) : (
                      <div style={{ color: MUTED, fontSize: 14, marginBottom: 12, lineHeight: 1.5 }}>
                        Не удалось получить список с сервера. Выберите диск вручную или нажмите «Обновить список».
                      </div>
                    )}
                    <label style={{ display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 280 }}>
                      <span style={LABEL_FIELD}>Диск вручную</span>
                      <select
                        value={draftDrive}
                        disabled={!!status?.running || !!actionLoading}
                        onChange={(e) => {
                          draftTouchedRef.current = true;
                          setDraftDrive(e.target.value);
                        }}
                        style={{
                          padding: '10px 12px',
                          borderRadius: 10,
                          border: `1px solid ${GLASS_BORDER}`,
                          background: INPUT_BG,
                          color: TEXT,
                          fontSize: 16,
                          fontWeight: 600,
                        }}
                      >
                        {'DEFGHIJKLMNOPQRSTUVWXYZ'.split('').map((letter) => (
                          <option key={letter} value={`${letter}:`}>
                            {letter}: — диск данных
                          </option>
                        ))}
                      </select>
                    </label>
                    <div style={{ marginTop: 12 }}>
                      <ActionBtn
                        icon={FiRefreshCw}
                        onClick={() => loadVolumes()}
                        disabled={volumesLoading}
                        variant="ghost"
                      >
                        {volumesLoading ? '…' : 'Обновить список дисков'}
                      </ActionBtn>
                    </div>
                  </div>
                ) : null}
              </div>
              {volumes.length > 0 ? (
                <div style={{ marginTop: 12 }}>
                  <ActionBtn
                    icon={FiRefreshCw}
                    onClick={() => loadVolumes()}
                    disabled={volumesLoading}
                    variant="ghost"
                  >
                    {volumesLoading ? '…' : 'Обновить список дисков'}
                  </ActionBtn>
                </div>
              ) : null}
            </GlassPanel>

            <GlassPanel style={{ marginBottom: 18 }}>
              <PanelTitle icon={FiClock} subtitle="Образ C: и учётные записи — ежедневно">
                Расписание
              </PanelTitle>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                  gap: 20,
                }}
              >
                <div
                  style={INNER_GLOSS}
                >
                  <div style={{ fontSize: 14, fontWeight: 600, color: TEXT, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <FiHardDrive size={16} color={ACCENT} /> Образ C:
                  </div>
                  <ScheduleFields
                    hour={draftHour}
                    minute={draftMinute}
                    onHour={setDraftHour}
                    onMinute={setDraftMinute}
                    disabled={!!status?.running}
                    timeLabel={`→ ${pad2(draftHour)}:${pad2(draftMinute)} каждый день`}
                  />
                </div>
                <div
                  style={INNER_GLOSS}
                >
                  <div style={{ fontSize: 14, fontWeight: 600, color: TEXT, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <FiUsers size={16} color={ACCENT} /> Учётки
                  </div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={draftAccountsEnabled}
                      onChange={(e) => setDraftAccountsEnabled(e.target.checked)}
                    />
                    <span style={{ fontSize: 13, color: STRONG }}>Автоматически</span>
                  </label>
                  <ScheduleFields
                    hour={draftAccountsHour}
                    minute={draftAccountsMinute}
                    onHour={setDraftAccountsHour}
                    onMinute={setDraftAccountsMinute}
                    disabled={!draftAccountsEnabled}
                    timeLabel={`→ ${pad2(draftAccountsHour)}:${pad2(draftAccountsMinute)}`}
                  />
                  <label style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 12 }}>
                    <span style={LABEL_FIELD}>Хранить файлов</span>
                    <input
                      type="number"
                      min={1}
                      max={365}
                      value={draftAccountsKeep}
                      disabled={!draftAccountsEnabled}
                      onChange={(e) => setDraftAccountsKeep(Number(e.target.value))}
                      style={{ ...INPUT_STYLE, width: 100 }}
                    />
                  </label>
                  <div style={{ fontSize: 12, color: MUTED, marginTop: 8 }}>{draftDrive}\MesendgerBackups\accounts\</div>
                </div>
              </div>
            </GlassPanel>

            <GlassPanel style={{ marginBottom: 18 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                <FiTrash2 color="#f87171" size={18} />
                <div style={{ fontSize: 15, fontWeight: 600 }}>Очистка старых образов</div>
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={draftRetentionEnabled}
                  onChange={(e) => setDraftRetentionEnabled(e.target.checked)}
                  disabled={!!status?.running}
                />
                <span style={{ fontSize: 14, color: '#334155' }}>Автоматически удалять старые образы</span>
              </label>
              <div style={{ fontSize: 12, color: '#b45309', margin: '-6px 0 14px', lineHeight: 1.45 }}>
                Каталог Windows Server Backup этой кнопкой больше не чистится: «оставить 1 копию» сносит полный образ C: вместе с инкрементом. На F: старый образ убирается только когда места уже не хватает — перед новым полным прогоном в 03:00.
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginBottom: 14 }}>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <span style={LABEL_FIELD}>Оставлять последних (шт.)</span>
                  <input
                    type="number"
                    min={1}
                    max={60}
                    value={draftKeepCount}
                    disabled={!draftRetentionEnabled || !!status?.running}
                    onChange={(e) => setDraftKeepCount(Number(e.target.value))}
                    style={{ ...INPUT_STYLE, width: 100, fontWeight: 600 }}
                  />
                </label>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <span style={LABEL_FIELD}>Старше (дней), 0 = выкл.</span>
                  <input
                    type="number"
                    min={0}
                    max={365}
                    value={draftMaxAgeDays}
                    disabled={!draftRetentionEnabled || !!status?.running}
                    onChange={(e) => setDraftMaxAgeDays(Number(e.target.value))}
                    style={{ ...INPUT_STYLE, width: 100, fontWeight: 600 }}
                  />
                </label>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <span style={LABEL_FIELD}>Автоочистка — час</span>
                  <input
                    type="number"
                    min={0}
                    max={23}
                    value={draftCleanupHour}
                    disabled={!draftRetentionEnabled}
                    onChange={(e) => setDraftCleanupHour(Number(e.target.value))}
                    style={{ ...INPUT_STYLE, width: 100, fontWeight: 600 }}
                  />
                </label>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <span style={LABEL_FIELD}>Автоочистка — минуты</span>
                  <input
                    type="number"
                    min={0}
                    max={59}
                    value={draftCleanupMinute}
                    disabled={!draftRetentionEnabled}
                    onChange={(e) => setDraftCleanupMinute(Number(e.target.value))}
                    style={{ ...INPUT_STYLE, width: 100, fontWeight: 600 }}
                  />
                </label>
                <div style={{ fontSize: 13, color: '#c4b5fd', fontWeight: 600, paddingTop: 18 }}>
                  → {pad2(draftCleanupHour)}:{pad2(draftCleanupMinute)} ежедневно
                </div>
              </div>
              {status?.retentionPreview ? (
                <div style={{ ...HINT_TEXT, marginBottom: 12 }}>
                  На диске {status.successCount ?? '—'} образов.
                  {status.retentionPreview.toDeleteCount > 0
                    ? ` К удалению: ${status.retentionPreview.toDeleteCount}.`
                    : ' Удалять пока нечего.'}
                </div>
              ) : null}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 10 }}>
                <ActionBtn icon={FiInfo} onClick={runCleanupPreview} disabled={!!actionLoading || !draftRetentionEnabled} variant="ghost">
                  {actionLoading === 'preview' ? '…' : 'Просмотр удаления'}
                </ActionBtn>
                <ActionBtn icon={FiTrash2} onClick={() => runCleanupNow(false)} disabled={!!actionLoading || !draftRetentionEnabled || !!status?.running} variant="danger">
                  {actionLoading === 'cleanup' ? '…' : 'Очистить сейчас'}
                </ActionBtn>
              </div>
              {cleanupPreview?.toDelete?.length ? (
                <div style={{ fontSize: 11, color: MUTED, maxHeight: 100, overflow: 'auto' }}>
                  {cleanupPreview.toDelete.map((v) => (
                    <div key={v.version}>• {v.version}</div>
                  ))}
                </div>
              ) : null}
              <div style={{ ...HINT_TEXT, marginTop: 8 }}>
                Самый новый образ всегда сохраняется. BAT (без мессенджера): Cleanup-Preview.bat, Cleanup-Now.bat, Install-Cleanup-Schedule.bat
              </div>
            </GlassPanel>

            <GlassPanel>
              <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 12 }}>Применить</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 14 }}>
                <ActionBtn
                  icon={FiSave}
                  onClick={() => saveSettings(false)}
                  disabled={!!actionLoading || !!status?.running}
                  variant="ghost"
                >
                  {actionLoading === 'save' ? '…' : 'Сохранить черновик'}
                </ActionBtn>
                <ActionBtn
                  icon={FiCheckCircle}
                  onClick={() => saveSettings(true)}
                  disabled={!!actionLoading || !!status?.running}
                  variant="success"
                >
                  {actionLoading === 'apply' ? 'Применение…' : 'Сохранить и применить в Windows'}
                </ActionBtn>
              </div>
              <div style={{ fontSize: 12, color: MUTED, lineHeight: 1.6 }}>
                <div><strong style={{ color: '#334155' }}>Образ системы:</strong> {draftDrive}\WindowsImageBackup\</div>
                <div><strong style={{ color: '#334155' }}>Учётки:</strong> {draftDrive}\MesendgerBackups\accounts\</div>
                <div><strong style={{ color: '#334155' }}>Логи:</strong> {draftDrive}\BackupLogs\</div>
                <div style={{ marginTop: 8 }}>«Применить» перенастраивает Windows Server Backup и задачи планировщика.</div>
              </div>
            </GlassPanel>
          </>
        )}

        {tab === 'history' && (
          <>
            <GlassPanel style={{ marginBottom: 14 }}>
              <PanelTitle icon={FiClock}>Журнал бэкапов образа C:</PanelTitle>
              {!historyRuns.length ? (
                <div style={{ color: MUTED, fontSize: 14 }}>
                  Записей пока нет. После первого бэкапа здесь появятся время, результат и длительность.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {historyRuns.map((run, idx) => {
                    const ok = run.success === true || String(run.result || '').toLowerCase().includes('success');
                    const fail = String(run.result || '').toLowerCase().includes('fail');
                    return (
                      <div
                        key={`${run.startedAt}-${idx}`}
                        className="backup-list-row"
                        style={{
                          background: ok
                            ? 'linear-gradient(180deg, rgba(255,255,255,0.92) 0%, rgba(236,253,245,0.62) 100%)'
                            : fail
                              ? 'linear-gradient(180deg, rgba(255,255,255,0.92) 0%, rgba(254,242,242,0.62) 100%)'
                              : undefined,
                        }}
                      >
                        <div style={{ fontWeight: 600, color: ok ? OK : fail ? DANGER : TEXT }}>
                          {ok ? 'Успешно' : fail ? 'Ошибка' : (run.result || 'Выполняется')}
                        </div>
                        <div style={{ fontSize: 12, color: MUTED, marginTop: 6, lineHeight: 1.55 }}>
                          <div>Старт: {formatIsoRu(run.startedAt)}</div>
                          {run.finishedAt ? <div>Конец: {formatIsoRu(run.finishedAt)}</div> : null}
                          {run.durationSeconds != null ? (
                            <div>Длительность: {formatDuration(run.durationSeconds)}</div>
                          ) : null}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </GlassPanel>

            <GlassPanel>
              <PanelTitle icon={FiHardDrive} subtitle={`Точки восстановления на диске ${targetDrive}`}>
                Точки восстановления
              </PanelTitle>
              {!restoreVersions.length ? (
                <div style={{ color: MUTED, fontSize: 14 }}>
                  {status?.versionsError
                    ? `Ошибка чтения списка образов: ${status.versionsError}`
                    : 'Список версий wbadmin недоступен без прав админа. Точка восстановления есть — см. журнал выше.'}
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {restoreVersions.map((v) => (
                    <div
                      key={v.version}
                      className="backup-list-row"
                    >
                      <div style={{ fontWeight: 600, color: TEXT }}>{v.version}</div>
                      <div style={{ fontSize: 12, color: MUTED, marginTop: 4 }}>
                        {v.backupTime ? formatIsoRu(v.backupTime) : '—'}
                        {v.canRecover !== false ? ' · можно восстановить' : ''}
                        {v.durationSeconds != null ? ` · ${formatDuration(v.durationSeconds)}` : ''}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </GlassPanel>
          </>
        )}

        {tab === 'logs' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            {[
              { title: 'Запуски бэкапа', lines: status?.logs?.runs, icon: FiPlay, open: true },
              { title: 'Настройка', lines: status?.logs?.setup, icon: FiSettings, open: false },
              { title: 'Бэкап учёток', lines: status?.logs?.accounts, icon: FiUsers, open: true },
              { title: 'Проверки', lines: status?.logs?.status, icon: FiCheckCircle, open: true },
              { title: 'Очистка старых', lines: status?.logs?.retention, icon: FiTrash2, open: false },
            ].map((block) => (
              <CollapsibleLogBlock
                key={block.title}
                title={block.title}
                icon={block.icon}
                lines={block.lines}
                defaultOpen={block.open}
              />
            ))}
          </div>
        )}
      </div>

      <style>{`
        @keyframes backupSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .backup-spin { animation: backupSpin 1.2s linear infinite; }
        .backup-modal-shell {
          position: relative;
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
        }
        .backup-gloss-card { transition: box-shadow 0.25s ease, transform 0.25s ease; }
        .backup-gloss-card-shine {
          position: absolute;
          inset: 0;
          border-radius: inherit;
          pointer-events: none;
          background: linear-gradient(165deg, rgba(255,255,255,0.52) 0%, rgba(255,255,255,0.12) 38%, transparent 62%);
          z-index: 0;
        }
        .backup-stat-tile {
          position: relative;
          padding: 18px 20px;
          border-radius: 20px;
          border: 1px solid rgba(255, 255, 255, 0.78);
          background: linear-gradient(165deg, rgba(255,255,255,0.92) 0%, rgba(248,250,255,0.78) 45%, rgba(237,233,254,0.52) 100%);
          box-shadow: 0 10px 36px rgba(99, 102, 241, 0.1), 0 2px 8px rgba(15, 23, 42, 0.04), inset 0 1px 0 rgba(255,255,255,0.98), inset 0 -1px 0 rgba(255,255,255,0.28);
          backdrop-filter: blur(20px) saturate(1.35);
          -webkit-backdrop-filter: blur(20px) saturate(1.35);
          overflow: hidden;
        }
        .backup-stat-tile::before {
          content: '';
          position: absolute;
          top: 0; left: 0; right: 0;
          height: 48%;
          background: linear-gradient(180deg, rgba(255,255,255,0.45) 0%, transparent 100%);
          pointer-events: none;
          border-radius: inherit;
        }
        .backup-stat-tile > * { position: relative; z-index: 1; }
        .backup-action-btn:not(:disabled):hover { transform: translateY(-1px); }
        .backup-action-primary:not(:disabled):hover { box-shadow: 0 14px 32px rgba(99,102,241,0.38), inset 0 1px 0 rgba(255,255,255,0.4), inset 0 -1px 0 rgba(67,56,202,0.22) !important; }
        .backup-action-ghost:not(:disabled):hover { box-shadow: 0 10px 22px rgba(99,102,241,0.14), inset 0 1px 0 rgba(255,255,255,0.98), inset 0 -1px 0 rgba(199,210,254,0.4) !important; }
        .backup-action-success:not(:disabled):hover { box-shadow: 0 10px 22px rgba(16,185,129,0.14), inset 0 1px 0 rgba(255,255,255,0.98), inset 0 -1px 0 rgba(167,243,208,0.5) !important; }
        .backup-action-danger:not(:disabled):hover { box-shadow: 0 10px 22px rgba(239,68,68,0.14), inset 0 1px 0 rgba(255,255,255,0.98), inset 0 -1px 0 rgba(254,202,202,0.45) !important; }
        .backup-tab-btn:not(.backup-tab-btn-active):hover {
          background: linear-gradient(180deg, rgba(255,255,255,0.55), rgba(237,233,254,0.35)) !important;
          color: #4338ca !important;
        }
        .backup-close-btn:hover { transform: scale(1.04); box-shadow: 0 8px 22px rgba(239,68,68,0.14), inset 0 1px 0 rgba(255,255,255,0.98) !important; }
        .backup-modal-shell ::selection { background: rgba(99,102,241,0.2); }
        .backup-modal-shell input:focus,
        .backup-modal-shell select:focus {
          border-color: rgba(167, 139, 250, 0.55) !important;
          box-shadow: 0 0 0 3px rgba(99,102,241,0.1), inset 0 1px 0 rgba(255,255,255,0.95), 0 4px 14px rgba(99,102,241,0.08) !important;
        }
        .backup-list-row {
          padding: 12px 14px;
          border-radius: 14px;
          border: 1px solid rgba(255, 255, 255, 0.78);
          background: linear-gradient(180deg, rgba(255,255,255,0.88) 0%, rgba(248,250,255,0.72) 100%);
          box-shadow: inset 0 1px 0 rgba(255,255,255,0.98), inset 0 -1px 0 rgba(255,255,255,0.25), 0 4px 12px rgba(99,102,241,0.05);
          backdrop-filter: blur(8px);
        }
      `}</style>
    </div>
  );
}
